/**
 * ContextSwitchManager - Shared Context Switching Logic for GLYDE SDK
 *
 * Manages context switching for both voice and text clients, providing:
 * - Pending context switch tracking (deferred until agent stops speaking)
 * - Smooth update (in-place prompt change) vs. full reinitialize patterns
 * - Fallback timeout handling
 *
 * This class is designed to be used by both GlydeVoice and GlydeText to share
 * context switching logic and ensure consistent behavior across modalities.
 *
 * @packageDocumentation
 */

import type { VoiceContextType, ContextSwitchPayload } from './types';

/**
 * Configuration for the context switch manager
 */
export interface ContextSwitchConfig {
  /** Timeout in ms before forcing context reinit (default: 30000) */
  fallbackTimeoutMs?: number;
  /** Callback when context switch is requested */
  onContextSwitchRequested?: (payload: ContextSwitchPayload) => void;
  /** Callback when context switch is applied */
  onContextSwitchApplied?: (contextType: VoiceContextType, contextId: string) => void;
  /** Callback when context switch fails */
  onContextSwitchFailed?: (error: Error) => void;
}

/**
 * Callbacks for executing the actual context switch operations
 */
export interface ContextSwitchCallbacks {
  /** Perform smooth update (e.g., UpdatePrompt for voice) */
  performSmoothUpdate: (contextType: VoiceContextType, contextId: string) => Promise<void>;
  /** Perform full reinitialize (stop + start) */
  performFullReinit: (contextType: VoiceContextType, contextId: string) => Promise<void>;
  /** Check if smooth update is possible (e.g., WebSocket is open) */
  canSmoothUpdate: () => boolean;
}

/**
 * Pending context switch state
 */
interface PendingContextSwitch {
  contextType: VoiceContextType;
  contextId: string;
}

/**
 * ContextSwitchManager - Manages context switching with smooth update and fallback patterns
 *
 * @example
 * ```typescript
 * const manager = new ContextSwitchManager({
 *   fallbackTimeoutMs: 30000,
 *   onContextSwitchApplied: (type, id) => console.log('Context switched:', type, id)
 * });
 *
 * manager.setCallbacks({
 *   performSmoothUpdate: async (type, id) => { ... },
 *   performFullReinit: async (type, id) => { ... },
 *   canSmoothUpdate: () => ws?.readyState === WebSocket.OPEN
 * });
 *
 * // When server requests context switch
 * manager.requestContextSwitch({ contextType: 'screening', contextId: 'app-uuid' });
 *
 * // When agent stops speaking (voice) or response received (text)
 * manager.flushPendingContextSwitch();
 * ```
 */
export class ContextSwitchManager {
  private static readonly DEFAULT_FALLBACK_TIMEOUT_MS = 30000;

  private config: ContextSwitchConfig;
  private callbacks: ContextSwitchCallbacks | null = null;
  private pendingSwitch: PendingContextSwitch | null = null;
  private fallbackTimer: ReturnType<typeof setTimeout> | null = null;
  private isSwitching = false;

  /**
   * Create a new ContextSwitchManager
   * @param config - Configuration options
   */
  constructor(config: ContextSwitchConfig = {}) {
    this.config = {
      fallbackTimeoutMs: config.fallbackTimeoutMs ?? ContextSwitchManager.DEFAULT_FALLBACK_TIMEOUT_MS,
      ...config
    };
  }

  /**
   * Set the callbacks for executing context switch operations
   * @param callbacks - Callback functions for smooth update and full reinit
   */
  setCallbacks(callbacks: ContextSwitchCallbacks): void {
    this.callbacks = callbacks;
  }

  /**
   * Request a context switch. The switch is deferred until flushPendingContextSwitch() is called
   * (typically when the agent stops speaking) or the fallback timeout fires.
   *
   * @param payload - Context switch payload from server
   */
  requestContextSwitch(payload: ContextSwitchPayload): void {
    const contextType = payload.contextType as VoiceContextType;
    const contextId = payload.contextId;

    console.log('[ContextSwitchManager] Context switch requested:', contextType, contextId);

    this.pendingSwitch = { contextType, contextId };

    // Notify listener
    if (this.config.onContextSwitchRequested) {
      this.config.onContextSwitchRequested(payload);
    }

    // Clear any existing fallback timer
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
    }

    // Set fallback timer in case flush is never called
    this.fallbackTimer = setTimeout(() => {
      this.fallbackTimer = null;
      console.log('[ContextSwitchManager] Fallback timeout reached, forcing context switch');
      this.flushPendingContextSwitch();
    }, this.config.fallbackTimeoutMs);

    console.log(
      '[ContextSwitchManager] Context switch pending; will apply when flushed or after',
      (this.config.fallbackTimeoutMs ?? ContextSwitchManager.DEFAULT_FALLBACK_TIMEOUT_MS) / 1000,
      's timeout'
    );
  }

  /**
   * Flush any pending context switch. Should be called when the agent stops speaking
   * (voice) or after receiving a response (text).
   *
   * Attempts smooth update first, falls back to full reinit if that fails.
   */
  async flushPendingContextSwitch(): Promise<void> {
    if (!this.pendingSwitch) {
      return;
    }

    if (this.isSwitching) {
      console.log('[ContextSwitchManager] Switch already in progress, skipping');
      return;
    }

    if (!this.callbacks) {
      console.error('[ContextSwitchManager] No callbacks set, cannot execute context switch');
      return;
    }

    // Clear fallback timer
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    }

    const { contextType, contextId } = this.pendingSwitch;
    this.pendingSwitch = null;
    this.isSwitching = true;

    console.log('[ContextSwitchManager] Applying context switch:', contextType, contextId);

    try {
      // Try smooth update first
      if (this.callbacks.canSmoothUpdate()) {
        try {
          await this.callbacks.performSmoothUpdate(contextType, contextId);
          console.log('[ContextSwitchManager] Smooth context update succeeded');
          this.config.onContextSwitchApplied?.(contextType, contextId);
          return;
        } catch (smoothError) {
          console.warn('[ContextSwitchManager] Smooth update failed, falling back to full reinit:', smoothError);
        }
      }

      // Fall back to full reinit
      await this.callbacks.performFullReinit(contextType, contextId);
      console.log('[ContextSwitchManager] Full reinit succeeded');
      this.config.onContextSwitchApplied?.(contextType, contextId);

    } catch (error) {
      console.error('[ContextSwitchManager] Context switch failed:', error);
      this.config.onContextSwitchFailed?.(error instanceof Error ? error : new Error(String(error)));
    } finally {
      this.isSwitching = false;
    }
  }

  /**
   * Cancel any pending context switch
   */
  cancelPendingSwitch(): void {
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    }
    if (this.pendingSwitch) {
      console.log('[ContextSwitchManager] Pending context switch cancelled');
      this.pendingSwitch = null;
    }
  }

  /**
   * Check if there is a pending context switch
   */
  hasPendingSwitch(): boolean {
    return this.pendingSwitch !== null;
  }

  /**
   * Get the pending context switch details
   */
  getPendingSwitch(): PendingContextSwitch | null {
    return this.pendingSwitch ? { ...this.pendingSwitch } : null;
  }

  /**
   * Check if a context switch is currently in progress
   */
  isSwitchInProgress(): boolean {
    return this.isSwitching;
  }

  /**
   * Process a function result that may contain a context switch request
   * @param output - Function output (string or object)
   * @returns True if a context switch was requested
   */
  processContextSwitchFromOutput(output: string | object): boolean {
    try {
      const parsed = typeof output === 'string' ? JSON.parse(output) : output;

      if (
        parsed &&
        typeof parsed === 'object' &&
        parsed.context_switch &&
        typeof parsed.context_switch.contextType === 'string' &&
        typeof parsed.context_switch.contextId === 'string'
      ) {
        const sw = parsed.context_switch as ContextSwitchPayload;
        this.requestContextSwitch(sw);
        return true;
      }
    } catch {
      // Not JSON or doesn't contain context_switch
    }
    return false;
  }

  /**
   * Clean up resources (timers, etc.)
   */
  destroy(): void {
    this.cancelPendingSwitch();
    this.callbacks = null;
  }
}
